/**
 * The Overview of a team, a specialist or one member of a team (`GET /teams/v1/:teamId[/members/:member]/overview`,
 * packages/shared/src/schemas/memberOverview.ts): what the agent is for, when it works, what it finishes and costs, how
 * often its runs succeed against the error budget, and what waits on the person reading. A part with nothing reported
 * behind it says so in words, never as a zero. The range is in the address (`?range=7d`, or `since` and `until`).
 */

import { MEMBER_OVERVIEW_RANGES, type MemberOverview } from '@aioe/shared';
import { Calendar, ChartColumn, CircleCheck, CircleDollar, Clock, Hourglass, ListCheck, Persons, Plus, Speedometer, Tag, Xmark } from '@gravity-ui/icons';
import { Button, Chip, Popover } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { routes } from '../../api/client.ts';
import { useLoad, useNow } from '../../hooks.ts';
import { useMoney } from '../../money.tsx';
import { ErrorNote, inputClass, ListBlock, ListGroup } from '../ui.tsx';
import { ArtEmpty, ArtSection, ArtStat, type ArtTone } from '../ui/Art.tsx';
import { Hint } from '../ui/Hint.tsx';
import { LoadingState } from '../ui/LoadingState.tsx';
import { AgeBars, BurnChart, Heatmap, HeatLegend } from './OverviewCharts.tsx';
import { compact, isRangeKey, NOT_REPORTED, openWords, percent, plural, RANGE_LABELS, RANGE_WORDS, runWords } from './overviewModel.ts';
import { saveTeamChange } from './teamSave.ts';
import './overview.css';

/** The period in the address: a range by its key, or a range of one's own. */
function usePeriod() {
  const [params, setParams] = useSearchParams();
  const since = params.get('since') ?? undefined;
  const until = params.get('until') ?? undefined;
  const raw = params.get('range');
  const range = since ? undefined : isRangeKey(raw) ? raw : '30d';
  const set = (next: { range?: string; since?: string; until?: string }) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        for (const k of ['range', 'since', 'until']) n.delete(k);
        if (next.since) {
          n.set('since', next.since);
          if (next.until) n.set('until', next.until);
        } else if (next.range && next.range !== '30d') n.set('range', next.range);
        return n;
      },
      { replace: true },
    );
  return { range, since, until, set };
}

const dateInput = (iso: string | undefined) => (iso ? iso.slice(0, 10) : '');
const dayWords = (iso: string) => new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

/** 1h 1d 7d 30d 60d 90d, and a range of one's own in a popover. */
function RangePicker({ period }: { period: ReturnType<typeof usePeriod> }) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(dateInput(period.since));
  const [to, setTo] = useState(dateInput(period.until));
  const valid = from && to && from <= to;
  return (
    <div className="flex items-center gap-1.5">
      <div className="-m-1 max-w-full overflow-x-auto p-1">
        <Segment aria-label="Range" size="sm" selectedKey={period.range ?? ''} onSelectionChange={(k) => period.set({ range: String(k) })} className="w-fit min-w-max">
          {MEMBER_OVERVIEW_RANGES.map((r) => (
            <Segment.Item key={r} id={r}>
              {RANGE_LABELS[r]}
            </Segment.Item>
          ))}
        </Segment>
      </div>
      <Popover isOpen={open} onOpenChange={setOpen}>
        <Hint text={period.since ? `From ${dayWords(period.since)} to ${dayWords(period.until ?? new Date().toISOString())}` : 'A range of your own'}>
          <Button size="sm" variant={period.since ? 'secondary' : 'ghost'} isIconOnly aria-label="A range of your own">
            <Calendar className="size-4" />
          </Button>
        </Hint>
        <Popover.Content placement="bottom end" className="w-[17rem] max-w-[calc(100vw-1rem)]">
          <Popover.Dialog className="grid gap-3 p-3" aria-label="A range of your own">
            <label className="grid gap-1 text-xs">
              <span className="font-medium">From</span>
              <input type="date" className={inputClass} value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="grid gap-1 text-xs">
              <span className="font-medium">To</span>
              <input type="date" className={inputClass} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="tertiary" onPress={() => setOpen(false)}>Cancel</Button>
              <Button
                size="sm"
                isDisabled={!valid}
                onPress={() => {
                  // Whole days where the reader is: from the first one's start to the last one's end.
                  const since = new Date(`${from}T00:00:00`).toISOString();
                  const until = new Date(new Date(`${to}T00:00:00`).getTime() + 86_400_000).toISOString();
                  period.set({ since, until: until > new Date().toISOString() ? new Date().toISOString() : until });
                  setOpen(false);
                }}
              >
                Show
              </Button>
            </div>
          </Popover.Dialog>
        </Popover.Content>
      </Popover>
    </div>
  );
}

/** What an agent is for and what it must not decide, with its skills as tags; those who may change it add and remove them. */
function AgentCard({ o, onChanged }: { o: MemberOverview; onChanged: () => void }) {
  const s = o.subject;
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [tag, setTag] = useState('');
  const [error, setError] = useState<string>();
  const kind = s.kind === 'specialist' ? 'specialist' : 'team';
  const memberName = s.memberName ?? s.name;
  const change = async (fn: (skills: string[]) => string[]) => {
    setError(undefined);
    try {
      const notes = await saveTeamChange(s.teamId, kind, (d) => ({ ...d, members: d.members.map((m, i) => ((s.memberName ? m.name === memberName : i === 0) ? { ...m, skills: fn(m.skills ?? []) } : m)) }));
      if (notes.length) setError(notes.join(' '));
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const label = s.kind === 'specialist' ? 'Specialist overview' : 'Agent overview';
  return (
    <ListGroup title={label} description={s.scope?.from === 'instructions' ? 'What it is for, from its instructions, and its skills.' : 'What it is for and its skills.'}>
      <ListBlock>
        {s.scope ? (
          <div className="grid justify-items-start gap-1">
            <p className="ov__scope text-sm" data-open={open || undefined}>{s.scope.text}</p>
            {s.scope.text.length > 320 ? (
              <button type="button" className="cursor-[var(--cursor-interactive)] text-xs text-accent hover:underline" onClick={() => setOpen((x) => !x)}>
                {open ? 'Show less' : 'Show all'}
              </button>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted">No description yet. Its owner writes what it owns and what it must never decide when they edit it.</p>
        )}
      </ListBlock>
      <ListBlock>
        <div className="ov__tags" aria-label="Skills">
          {s.tags.map((t) => (
            <Chip key={t} size="sm" variant="secondary">
              <Tag className="size-3" aria-hidden="true" />
              <Chip.Label>{t}</Chip.Label>
              {s.mayEdit ? (
                <button type="button" className="ms-0.5 inline-flex cursor-[var(--cursor-interactive)] rounded-full text-muted hover:text-foreground" aria-label={`Remove ${t}`} onClick={() => void change((xs) => xs.filter((x) => x !== t))}>
                  <Xmark className="size-3" />
                </button>
              ) : null}
            </Chip>
          ))}
          {s.mayEdit ? (
            adding ? (
              <form
                className="flex items-center gap-1.5"
                onSubmit={(e) => {
                  e.preventDefault();
                  const t = tag.trim();
                  if (t && !s.tags.includes(t)) void change((xs) => [...xs, t]);
                  setTag('');
                  setAdding(false);
                }}
              >
                <input className={`${inputClass} h-7 w-40 py-0 text-xs`} aria-label="New tag" placeholder="A skill, in a few words" value={tag} maxLength={100} onChange={(e) => setTag(e.target.value)} autoFocus onBlur={() => !tag && setAdding(false)} />
              </form>
            ) : (
              <Button size="sm" variant="ghost" className="h-7 text-muted" onPress={() => setAdding(true)}>
                <Plus className="size-3.5" />
                Add tag
              </Button>
            )
          ) : s.tags.length ? null : (
            <span className="text-xs text-muted">No skills named.</span>
          )}
        </div>
        {error ? (
          <div className="mt-2">
            <ErrorNote error={error} />
          </div>
        ) : null}
      </ListBlock>
    </ListGroup>
  );
}

function Tiles({ o, tone }: { o: MemberOverview; tone: ArtTone }) {
  const { format } = useMoney();
  const t = o.tiles;
  const team = o.subject.kind === 'team';
  // Metered spend only (costs.ts): calls on paid seats are not spend, so they are counted in the calls, not the money.
  const spendDetail = t.spend.reported ? `${compact(t.spend.tokens)} tokens · ${plural(t.spend.calls, 'call', 'calls')}` : NOT_REPORTED;
  const spend = <ArtStat tone={tone} icon={<CircleDollar />} label="Model spend" value={t.spend.reported ? format(t.spend.usd) : undefined} detail={spendDetail} />;
  if (team) {
    const limit = t.nearestLimit;
    const near = limit?.percent !== null && limit?.percent !== undefined;
    return (
      <div className="ov__tiles">
        <ArtStat tone={tone} icon={<CircleCheck />} label="Completion rate" value={t.completion.rate === null ? undefined : percent(t.completion.rate)} detail={t.completion.total ? `${t.completion.done} completed · ${t.completion.notDone} not finished` : 'Nothing closed in this range'} />
        <ArtStat tone={tone} icon={<ListCheck />} label="Open work" value={t.activeWork ? t.activeWork.onBoard.toLocaleString() : undefined} detail={t.activeWork ? `${t.activeWork.running} running · ${t.activeWork.inReview} in review` : ''} />
        <ArtStat
          tone={tone}
          icon={<Speedometer />}
          label="Nearest limit"
          value={near ? `${Math.round(limit.percent!)}%` : 'None'}
          detail={near ? `of ${limit.name ?? `a budget on ${limit.scope ?? 'it'}`}` : 'No budget applies to this team'}
          needsYou={near && limit.state !== 'ok'}
        />
        {spend}
      </div>
    );
  }
  return (
    <div className="ov__tiles">
      <ArtStat tone={tone} icon={<CircleCheck />} label="Task completion" value={t.completion.rate === null ? undefined : percent(t.completion.rate)} detail={t.completion.total ? `${t.completion.done} of ${plural(t.completion.total, 'task', 'tasks')}` : 'No tasks in this range'} />
      <ArtStat tone={tone} icon={<ListCheck />} label="Open tasks" value={(t.open.active + t.open.queued + t.open.waiting).toLocaleString()} detail={openWords(t.open)} />
      {spend}
      <ArtStat
        tone={tone}
        icon={<ChartColumn />}
        label="Cost per task"
        value={t.costPerTask.reported && t.costPerTask.medianUsd !== null ? format(t.costPerTask.medianUsd) : undefined}
        detail={!t.costPerTask.reported ? NOT_REPORTED : t.costPerTask.medianUsd === null ? 'No task finished in this range' : `per completed task · median of ${t.costPerTask.tasks}`}
      />
    </div>
  );
}

function RunSuccess({ o, tone }: { o: MemberOverview; tone: ArtTone }) {
  const r = o.runSuccess;
  const title = 'Run success';
  const hint = `Target ${percent(r.target)} · ${r.windowDays}-day window`;
  if (!r.reported) {
    return (
      <ArtSection tone={tone} icon={<CircleCheck />} title={title} hint={hint}>
        <ArtEmpty tone={tone} icon={<CircleCheck />} compact title="No runs reported in the last 30 days">
          When this {o.subject.kind === 'team' ? "team's" : "agent's"} machines report finished runs, how many succeed shows here against the target.
        </ArtEmpty>
      </ArtSection>
    );
  }
  const used = r.allowedFailures > 0 ? Math.min(1, r.failed / r.allowedFailures) : r.failed > 0 ? 1 : 0;
  return (
    <ArtSection tone={tone} icon={<CircleCheck />} title={title} hint={hint}>
      <div className="ov-run">
        <div className="ov-run__figures">
          <div>
            <div className="ov-figure__label">Success rate</div>
            <div className="ov-figure__value">{r.rate === null ? '—' : percent(r.rate)}</div>
            <div className="ov-figure__detail">{runWords(r)}</div>
          </div>
          <div>
            <div className="ov-figure__label">Error budget left</div>
            <div className="ov-figure__value">{percent(r.budgetLeft)}</div>
            <div className="ov-figure__detail">
              Used {r.failed} of {r.allowedFailures.toLocaleString()} {r.allowedFailures === 1 ? 'failure' : 'failures'}
            </div>
            <div className="ov-meter" role="meter" aria-label="Error budget used" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(used * 100)}>
              <span className="ov-meter__used" data-spent={used >= 1 || undefined} style={{ width: `${Math.max(used > 0 ? 3 : 0, used * 100)}%` }} />
            </div>
          </div>
          <div>
            <div className="ov-figure__label">At this pace</div>
            <div className="ov-figure__detail text-foreground">
              {r.budgetLeft <= 0 ? 'The budget is spent: every further failure in the window is past the target.' : `${plural(r.failuresLeft, 'more failure', 'more failures')} before the budget is spent.`}
            </div>
          </div>
        </div>
        <BurnChart run={r} />
      </div>
    </ArtSection>
  );
}

function Waiting({ o, now }: { o: MemberOverview; now: number }) {
  const tone: ArtTone = 'amber';
  return (
    <ArtSection tone={tone} icon={<Hourglass />} title="Waiting for you" count={o.waiting.total || undefined} hint={o.waiting.total > o.waiting.items.length ? `the ${o.waiting.items.length} oldest` : undefined}>
      {o.waiting.items.length ? (
        <AgeBars items={o.waiting.items} now={now} />
      ) : (
        <ArtEmpty tone={tone} icon={<Hourglass />} compact title="Nothing waits on you">
          Approvals, questions and blocked tasks from {o.subject.kind === 'team' ? 'this team' : o.subject.name} that need a person show here, with how long each has waited.
        </ArtEmpty>
      )}
    </ArtSection>
  );
}

export function TeamOverview({ teamId, member, tone, onLoaded }: { teamId: string; member?: string; tone: ArtTone; onLoaded?: (o: MemberOverview) => void }) {
  const period = usePeriod();
  const now = useNow();
  const o = useLoad(
    () =>
      routes.teamOverview(teamId, period.since ? { since: period.since, ...(period.until ? { until: period.until } : {}) } : { range: period.range ?? '30d' }, member).then((x) => {
        onLoaded?.(x);
        return x;
      }),
    [teamId, member, period.range, period.since, period.until],
    120_000,
    { whenVisible: true },
  );
  const data = o.data;
  const who = data?.subject.kind === 'team' ? 'the team' : (data?.subject.name ?? 'it');
  const rangeWords = period.since ? `${dayWords(period.since)} to ${dayWords(period.until ?? new Date(now).toISOString())}` : RANGE_WORDS[period.range ?? '30d'].replace(/^t/, 'T');
  return (
    <div className="ov">
      <div className="ov__toolbar">
        <p className="text-xs text-muted">
          {rangeWords}
          {data ? ` · times in ${data.range.timeZone}` : ''}
        </p>
        <RangePicker period={period} />
      </div>
      {!data ? (
        o.error ? <ErrorNote error={o.error} /> : <LoadingState variant="inline" skeleton="rows" label="Reading the overview…" />
      ) : (
        <>
          <ErrorNote error={o.error} />
          {data.subject.kind !== 'team' ? <AgentCard o={data} onChanged={o.reload} /> : null}
          <div className="ov__grid ov__grid--top">
            <ArtSection
              tone={tone}
              icon={data.subject.kind === 'team' ? <Persons /> : <Clock />}
              title={`When ${who} works`}
              hint={data.heatmap.reported ? `${plural(data.heatmap.calls, 'model call', 'model calls')}, by weekday and hour` : undefined}
              actions={data.heatmap.reported && data.heatmap.calls ? <HeatLegend /> : undefined}
            >
              {data.heatmap.reported ? (
                data.heatmap.calls ? (
                  <Heatmap heatmap={data.heatmap} timeZone={data.range.timeZone} />
                ) : (
                  <ArtEmpty tone={tone} icon={<Clock />} compact title="No model calls in this range">
                    Pick a longer range to see when {who} works.
                  </ArtEmpty>
                )
              ) : (
                <ArtEmpty tone={tone} icon={<Clock />} compact title={NOT_REPORTED}>
                  {data.notes[0] ?? 'When its machines say which member made each model call, the hours it works show here.'}
                </ArtEmpty>
              )}
            </ArtSection>
            <Tiles o={data} tone={tone} />
          </div>
          <div className="ov__grid ov__grid--bottom">
            <RunSuccess o={data} tone={tone} />
            <Waiting o={data} now={now} />
          </div>
        </>
      )}
    </div>
  );
}
